import Cookies from "js-cookie";

const READINESS_KEY = "prontopsy_device_readiness";
const CALL_READINESS_PREFIX = "prontopsy_call_device_ready_";
const READINESS_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const CALL_READINESS_MAX_AGE_MS = 2 * 60 * 1000;
const CONSULTANT_LOGIN_READINESS_KEY = "prontopsy_consultant_login_readiness";

export function setPersistentItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (_) {}
  try {
    Cookies.set(key, value, { expires: 365, path: '/' });
  } catch (_) {}
}

export function getPersistentItem(key) {
  try {
    const val = localStorage.getItem(key);
    if (val !== null) return val;
  } catch (_) {}
  try {
    const val = Cookies.get(key);
    if (val !== undefined && val !== null) {
      try {
        localStorage.setItem(key, val);
      } catch (_) {}
      return val;
    }
  } catch (_) {}
  return null;
}

export function normalizeCallMode(mode) {
  return String(mode || "video").toLowerCase() === "voice" ? "voice" : "video";
}

export function requiredMediaForMode(mode) {
  const normalized = normalizeCallMode(mode);
  return {
    mode: normalized,
    audio: true,
    video: normalized === "video",
  };
}

export function saveDeviceReadiness(result = {}) {
  try {
    let existing = {};
    try {
      const raw = getPersistentItem(READINESS_KEY);
      existing = raw ? JSON.parse(raw) : {};
    } catch (_) {}
    const isVoiceOnly = result.audio !== undefined && result.video === undefined;
    const isVideoOnly = result.video !== undefined && result.audio === undefined;

    const finalAudio = isVideoOnly
      ? existing.audio === true
      : (result.audio === true || (result.audio === undefined && existing.audio === true));
    const finalVideo = isVoiceOnly
      ? existing.video === true
      : (result.video === true || (result.video === undefined && existing.video === true));

    const data = JSON.stringify({
      checkedAt: Date.now(),
      audio: finalAudio,
      video: finalVideo,
    });
    setPersistentItem(READINESS_KEY, data);
  } catch (_) {
    /* ignore storage failures */
  }
}

export function hasFreshDeviceReadiness(mode = "video") {
  try {
    const raw = getPersistentItem(READINESS_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    if (!saved) {
      return false;
    }
    const required = requiredMediaForMode(mode);
    return saved.audio === true && (!required.video || saved.video === true);
  } catch (_) {
    return false;
  }
}

async function permissionState(name) {
  try {
    if (!navigator.permissions?.query) return "unsupported";
    const result = await navigator.permissions.query({ name });
    return result?.state || "unsupported";
  } catch (_) {
    return "unsupported";
  }
}

// Validate a cached grant without calling getUserMedia.
// 1. If browser permissions are already "granted" and hardware is connected, auto-verify.
// 2. If previously verified on this browser and permissions not denied, reuse status.
// 3. If hardware is detected and permissions are not denied, auto-verify and save status.
export async function hasUsableFreshDeviceReadiness(mode = "video") {
  const required = requiredMediaForMode(mode);
  const [microphonePermission, cameraPermission] = await Promise.all([
    permissionState("microphone"),
    required.video ? permissionState("camera") : Promise.resolve("granted"),
  ]);

  if (microphonePermission === "denied") return false;
  if (required.video && cameraPermission === "denied") return false;

  let hasMicrophone = true;
  let hasCamera = true;
  try {
    if (navigator.mediaDevices?.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (Array.isArray(devices) && devices.length > 0) {
        const audioInputs = devices.filter((device) => device.kind === "audioinput");
        const videoInputs = devices.filter((device) => device.kind === "videoinput");
        if (audioInputs.length > 0) hasMicrophone = true;
        if (videoInputs.length > 0) hasCamera = true;
      }
    }
  } catch (_) {}

  if (!hasMicrophone) return false;
  if (required.video && !hasCamera) return false;

  // If permissions are explicitly granted in the browser, auto-persist and consider ready
  if (microphonePermission === "granted" && (!required.video || cameraPermission === "granted")) {
    saveDeviceReadiness({
      audio: true,
      video: cameraPermission === "granted",
    });
    return true;
  }

  // If previously verified on this browser/device and permissions are not denied, reuse status
  if (hasFreshDeviceReadiness(mode)) {
    if (microphonePermission !== "denied" && (!required.video || cameraPermission !== "denied")) {
      return true;
    }
  }

  return false;
}

const LOGIN_PROBE_KEY = "prontopsy_login_device_probe";

export function profileOffersModes(profile) {
  const modes = { chat: false, voice: false, video: false };
  let hasFoundModes = false;
  const rows = []
    .concat(profile?.service || [])
    .concat(profile?.serviceData || [])
    .concat(profile?.serviceDataTableMap || []);
  const visit = (slug) => {
    const s = String(slug || "").toLowerCase();
    if (s === "voice" || s === "audio") { modes.voice = true; hasFoundModes = true; }
    if (s === "video") { modes.video = true; hasFoundModes = true; }
    if (s === "chat") { modes.chat = true; hasFoundModes = true; }
  };
  rows.forEach((row) => {
    const serviceObj = row?.service?.title ? row.service : (row?.service?.service?.title ? row.service.service : (row?.title ? row : null));
    const title = serviceObj?.title || row?.title;
    const titleStr = typeof title === "string" ? title : (title?.en || title?.it || title?.es || "");
    const slug = serviceObj?.slug || row?.service?.slug || row?.service?.service?.slug || row?.slug || "";

    const lowerTitle = titleStr.toLowerCase();
    const lowerSlug = String(slug).toLowerCase();

    const isDirectAccess =
      lowerTitle.includes("direct access") ||
      lowerTitle.includes("call now") ||
      lowerTitle.includes("accesso diretto") ||
      lowerTitle.includes("acceso directo") ||
      lowerTitle.includes("consulenza") ||
      lowerTitle.includes("consultation") ||
      lowerTitle.includes("chiamata rapida") ||
      lowerTitle.includes("llamada rapida") ||
      lowerSlug.includes("direct-access") ||
      lowerSlug.includes("call-now") ||
      lowerSlug.includes("accesso-diretto") ||
      lowerSlug.includes("acceso-directo") ||
      lowerSlug.includes("consulenza");

    if (isDirectAccess || (!lowerTitle && !lowerSlug && (row?.mode || row?.modes))) {
      const modeField = row?.mode || row?.modes;
      if (Array.isArray(modeField)) {
        modeField.forEach((m) => visit(m?.slug || m?.title?.en || m?.title || m));
      } else if (modeField) {
        visit(modeField?.slug || modeField?.title?.en || modeField);
      }
      visit(row?.service?.mode?.slug);
      visit(row?.mode?.slug);
    }
  });

  if (hasFoundModes) {
    return modes;
  }

  // By default, consultant offers all standard consultation modes
  return { chat: true, voice: true, video: true };
}

export async function probeDevices({ audio = false, video = false } = {}) {
  const result = { audio: false, video: false, fallbackCamera: false, fallbackMic: false };
  if (!audio && !video) return result;
  if (!navigator.mediaDevices?.getUserMedia) return result;

  const savedCameraId = getPersistentItem("prontopsy_last_camera_id");
  const savedMicId = getPersistentItem("prontopsy_last_microphone_id");

  const stop = (stream) => {
    try {
      stream?.getTracks?.().forEach((t) => {
        try {
          t.stop();
          t.enabled = false;
        } catch (_) {}
      });
    } catch (_) {
      /* ignore */
    }
  };

  // Check if saved devices are available
  let devices = [];
  try {
    if (navigator.mediaDevices.enumerateDevices) {
      devices = await navigator.mediaDevices.enumerateDevices();
    }
  } catch (_) {}

  const savedCameraExists = savedCameraId && devices.some(d => d.kind === "videoinput" && d.deviceId === savedCameraId);
  const savedMicExists = savedMicId && devices.some(d => d.kind === "audioinput" && d.deviceId === savedMicId);

  let useCameraConstraint = video ? (savedCameraExists ? { deviceId: { ideal: savedCameraId } } : true) : false;
  let useMicConstraint = audio ? (savedMicExists ? { deviceId: { ideal: savedMicId } } : true) : false;

  if (video && savedCameraId && !savedCameraExists) {
    result.fallbackCamera = true;
  }
  if (audio && savedMicId && !savedMicExists) {
    result.fallbackMic = true;
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: useMicConstraint,
      video: useCameraConstraint,
    });
    result.audio = !audio || stream.getAudioTracks().some((t) => t.readyState === "live");
    result.video = !video || stream.getVideoTracks().some((t) => t.readyState === "live");
    stop(stream);
  } catch (err) {
    // If exact constraint failed, try fallback to defaults
    let fallbackNeeded = false;
    let fallbackVideoConstraint = video ? true : false;
    let fallbackAudioConstraint = audio ? true : false;

    if (savedCameraExists && video) {
      fallbackNeeded = true;
      result.fallbackCamera = true;
    }
    if (savedMicExists && audio) {
      fallbackNeeded = true;
      result.fallbackMic = true;
    }

    if (fallbackNeeded) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: fallbackAudioConstraint,
          video: fallbackVideoConstraint,
        });
        result.audio = !audio || stream.getAudioTracks().some((t) => t.readyState === "live");
        result.video = !video || stream.getVideoTracks().some((t) => t.readyState === "live");
        stop(stream);
      } catch (fallbackErr) {
        // Fallback failed
      }
    } else {
      // If combined audio+video failed, try testing audio and video independently
      if (audio) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
          });
          result.audio = stream.getAudioTracks().some((t) => t.readyState === "live");
          stop(stream);
        } catch (_) {}
      }
      if (video) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: true,
          });
          result.video = stream.getVideoTracks().some((t) => t.readyState === "live");
          stop(stream);
        } catch (_) {}
      }
    }
  }

  // Save warning in sessionStorage to notify the user on dashboard/control panel mount
  if (result.fallbackCamera || result.fallbackMic) {
    try {
      const warnings = [];
      if (result.fallbackCamera) {
        const label = getPersistentItem("prontopsy_last_camera_label") || "Saved camera";
        warnings.push(`Previously saved camera "${label}" was not found. Fell back to system default.`);
      }
      if (result.fallbackMic) {
        const label = getPersistentItem("prontopsy_last_microphone_label") || "Saved microphone";
        warnings.push(`Previously saved microphone "${label}" was not found. Fell back to system default.`);
      }
      sessionStorage.setItem("prontopsy_device_fallback_warning", JSON.stringify(warnings));
    } catch (_) {}
  }

  return result;
}

export async function autoProbeInitialDeviceReadiness({ audio = false, video = false } = {}) {
  if (!audio && !video) return { audio: false, video: false };
  if (!navigator.mediaDevices?.getUserMedia) return { audio: false, video: false };

  // Check if permissions are explicitly denied
  const [micPerm, camPerm] = await Promise.all([
    audio ? permissionState("microphone") : Promise.resolve("granted"),
    video ? permissionState("camera") : Promise.resolve("granted"),
  ]);
  if (micPerm === "denied" && camPerm === "denied") {
    return { audio: false, video: false };
  }

  // Check hardware presence
  let hasMicrophone = true;
  let hasCamera = true;
  try {
    if (navigator.mediaDevices?.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      hasMicrophone = devices.some((d) => d.kind === "audioinput");
      hasCamera = devices.some((d) => d.kind === "videoinput");
    }
  } catch (_) {}

  const shouldProbeAudio = audio && micPerm !== "denied" && hasMicrophone;
  const shouldProbeVideo = video && camPerm !== "denied" && hasCamera;

  if (!shouldProbeAudio && !shouldProbeVideo) {
    return { audio: false, video: false };
  }

  try {
    const probe = await probeDevices({
      audio: shouldProbeAudio,
      video: shouldProbeVideo,
    });
    if (probe.audio || probe.video) {
      saveDeviceReadiness(probe);
    }
    return probe;
  } catch (_) {
    return { audio: false, video: false };
  }
}

export function saveLoginDeviceProbe(result) {
  try {
    sessionStorage.setItem(
      LOGIN_PROBE_KEY,
      JSON.stringify({ ...result, at: Date.now() }),
    );
  } catch (_) {
    /* ignore */
  }
}

export function takeLoginDeviceProbe() {
  try {
    const raw = sessionStorage.getItem(LOGIN_PROBE_KEY);
    if (!raw) return null;
    // Peek only — sidebar + dashboard both merge this. A failed probe
    // must never be the last writer of readyForAudio / readyForVideo.
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - parsed.at > 5 * 60 * 1000) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export function setConsultantLoginReadinessPending(pending) {
  try {
    if (pending) sessionStorage.setItem(CONSULTANT_LOGIN_READINESS_KEY, "pending");
    else sessionStorage.removeItem(CONSULTANT_LOGIN_READINESS_KEY);
  } catch (_) {
    /* ignore storage failures */
  }
}

export function isConsultantLoginReadinessPending() {
  try {
    return sessionStorage.getItem(CONSULTANT_LOGIN_READINESS_KEY) === "pending";
  } catch (_) {
    return false;
  }
}

export function clearDeviceReadiness() {
  try {
    localStorage.removeItem(READINESS_KEY);
  } catch (_) {}
  try {
    Cookies.remove(READINESS_KEY, { path: '/' });
  } catch (_) {}
}

export function saveCallDeviceReadiness(appointmentId, result = {}) {
  if (!appointmentId) return;
  try {
    sessionStorage.setItem(
      `${CALL_READINESS_PREFIX}${appointmentId}`,
      JSON.stringify({
        checkedAt: Date.now(),
        audio: result.audio === true,
        video: result.video === true,
      }),
    );
  } catch (_) {
    /* ignore storage failures */
  }
}

export function hasFreshCallDeviceReadiness(appointmentId, mode = "video") {
  if (!appointmentId) return false;
  try {
    const saved = JSON.parse(
      sessionStorage.getItem(`${CALL_READINESS_PREFIX}${appointmentId}`) || "null",
    );
    if (!saved?.checkedAt || Date.now() - saved.checkedAt > CALL_READINESS_MAX_AGE_MS) {
      return false;
    }
    const required = requiredMediaForMode(mode);
    return saved.audio === true && (!required.video || saved.video === true);
  } catch (_) {
    return false;
  }
}
